/**
 * Build a safe SELECT…FROM…JOIN SQL from a visual table-join config.
 * Aliases are a, b, c… Columns are selected as alias.col AS col (or alias_col on clash).
 *
 * Select roles:
 *   - no agg / isMetric=false → raw value (IDs, names, manufacturer_id, …)
 *   - agg SUM|AVG|MAX|MIN|COUNT → aggregated metric
 */

import {
  compileMathToSql,
  compileTemplateToSql,
  isCustomColumn,
  mathFieldKeys,
  sqlIdent,
} from './tableColumnDisplay';

const JOIN_HINT_COLS = [
  'personnel_id',
  'supervisor_id',
  'dc_id',
  'year_number',
  'month_number',
  'manufacturer_id',
  'product_group_id',
  'saleoffice_id',
  'job_id',
  'targetgroup_id',
];

/** When names differ: fact.manufacturer_id = catalog.id */
const JOIN_EQUIV = [
  ['manufacturer_id', 'manufacturer_id'],
  ['manufacturer_id', 'id'],
  ['product_group_id', 'product_group_id'],
  ['product_group_id', 'id'],
  ['dc_id', 'dc_id'],
  ['dc_id', 'id'],
  ['job_id', 'job_id'],
  ['job_id', 'id'],
  ['personnel_id', 'personnel_id'],
  ['personnel_id', 'id'],
  ['year_number', 'year_number'],
  ['month_number', 'month_number'],
];

const ALIAS_POOL = 'abcdefghijklmnopqrstuvwxyz'.split('');

const SESSION_PARAMS = new Set([
  ':year',
  ':month',
  ':months',
  ':personnel_id',
  ':personnel_code',
  ':dc_id',
  ':as_of_day',
  ':day',
  ':period',
  ':level_type',
]);

const PERIOD_PARAM_RE = /(^|[^:]):(year|month|months|as_of_day|day|period)\b/i;
const PERIOD_BRACE_RE = /\{\{\s*(year|month|months|as_of_day|day|period)\s*\}\}/i;

const WHERE_OPS = [
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: 'LIKE', label: 'LIKE' },
  { value: 'NOT LIKE', label: 'NOT LIKE' },
  { value: 'IS NULL', label: 'تهی' },
  { value: 'IS NOT NULL', label: 'غیرتهی' },
];

const WHERE_OP_SET = new Set(WHERE_OPS.map((o) => o.value).concat(['<>']));

function normCols(cols = []) {
  return [...new Set(cols.map((c) => String(c).toLowerCase()))];
}

/**
 * Suggested join pairs from left table columns → right table columns.
 * Returns [{ leftCol, rightCol }].
 */
function suggestJoins(leftCols = [], rightCols = []) {
  const leftSet = new Set(normCols(leftCols));
  const rightSet = new Set(normCols(rightCols));
  const pairs = [];
  const seen = new Set();

  const add = (l, r) => {
    if (!leftSet.has(l) || !rightSet.has(r)) return;
    const k = `${l}=${r}`;
    if (seen.has(k)) return;
    seen.add(k);
    pairs.push({ leftCol: l, rightCol: r });
  };

  JOIN_HINT_COLS.forEach((c) => add(c, c));
  JOIN_EQUIV.forEach(([l, r]) => add(l, r));

  if (!pairs.length) {
    [...leftSet].forEach((c) => add(c, c));
  }
  return pairs.slice(0, 8);
}

/**
 * Join hints against every already-added table (A, B, …), not only the primary.
 * Later tables win ties so C prefers B when both match equally.
 *
 * @param {Array<{ alias: string }>} previousTables
 * @param {Record<string, { all?: string[] }|string[]>} colMeta
 * @param {string[]} rightCols
 * @returns {{ all: Array<{leftAlias,leftCol,rightCol}>, preferredAlias: string|null, preferred: Array }}
 */
function suggestJoinsFromTables(previousTables = [], colMeta = {}, rightCols = []) {
  const all = [];
  const seen = new Set();
  let preferredAlias = previousTables[previousTables.length - 1]?.alias || null;
  let preferred = [];

  const colsOf = (alias) => {
    const meta = colMeta[alias];
    if (Array.isArray(meta)) return meta;
    return meta?.all || [];
  };

  for (let i = previousTables.length - 1; i >= 0; i -= 1) {
    const left = previousTables[i];
    if (!left?.alias) continue;
    const hints = suggestJoins(colsOf(left.alias), rightCols).map((h) => ({
      ...h,
      leftAlias: left.alias,
    }));
    if (hints.length > preferred.length) {
      preferred = hints;
      preferredAlias = left.alias;
    }
    hints.forEach((h) => {
      const key = `${h.leftAlias}.${h.leftCol}|${h.rightCol}`;
      if (seen.has(key)) return;
      seen.add(key);
      all.push(h);
    });
  }

  // Keep display order a → b → c
  all.sort((x, y) => {
    const ax = previousTables.findIndex((t) => t.alias === x.leftAlias);
    const ay = previousTables.findIndex((t) => t.alias === y.leftAlias);
    return ax - ay;
  });

  return { all, preferredAlias, preferred };
}

function pickAlias(used) {
  return ALIAS_POOL.find((a) => !used.has(a)) || `t${used.size}`;
}

function ident(name) {
  return String(name || '').replace(/[^a-z0-9_]/gi, '');
}

function aliasIdent(name) {
  return String(name || '').replace(/[^a-z]/gi, '').toLowerCase();
}

function colsOfMap(columnMap, alias) {
  const meta = columnMap?.[alias];
  if (Array.isArray(meta)) return meta;
  return meta?.all || [];
}

function hasKnownColumns(columnMap, primaryCols) {
  if (Array.isArray(primaryCols) && primaryCols.length) return true;
  return Object.values(columnMap || {}).some((c) => (Array.isArray(c) ? c.length : c?.all?.length));
}

function tableHasColumnKnown(cols, name) {
  if (!Array.isArray(cols) || !cols.length) return false;
  return cols.map((c) => String(c).toLowerCase()).includes(String(name).toLowerCase());
}

/** First alias (in FROM order) that actually has this column. */
function aliasForColumn(normalized, columnMap, primaryCols, colName, primaryAlias) {
  for (const t of normalized) {
    const cols = colsOfMap(columnMap, t.alias);
    if (tableHasColumnKnown(cols, colName)) return t.alias;
  }
  if (tableHasColumnKnown(primaryCols, colName)) return primaryAlias;
  if (!hasKnownColumns(columnMap, primaryCols)) return primaryAlias;
  return null;
}

function quoteSqlValue(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return null;
  const key = v.toLowerCase();
  if (SESSION_PARAMS.has(key)) return key;
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;
  return `'${v.replace(/'/g, "''")}'`;
}

function normalizeWhereOp(op) {
  const raw = String(op || '=').trim().toUpperCase().replace(/\s+/g, ' ');
  if (raw === '<>') return '!=';
  return raw;
}

function whereRowToSql(row, fallbackAlias) {
  if (!row) return null;
  const alias = aliasIdent(row.alias) || fallbackAlias;
  const col = ident(row.col);
  if (!alias || !col) return null;
  const op = normalizeWhereOp(row.op);
  if (!WHERE_OP_SET.has(op)) return null;
  if (op === 'IS NULL' || op === 'IS NOT NULL') {
    return `${alias}.${col} ${op}`;
  }
  const val = quoteSqlValue(row.value);
  if (val == null) return null;
  return `${alias}.${col} ${op} ${val}`;
}

/**
 * @param {object} cfg
 * @param {Array<{ name: string, alias?: string }>} cfg.tables
 * @param {Array<{ type?: string, leftAlias: string, leftCol: string, rightAlias: string, rightCol: string }>} cfg.joins
 * @param {Array<{ expr: string, alias?: string, isMetric?: boolean, agg?: string }>} cfg.selects
 * @param {object} [cfg.filters]
 * @param {Array<{ alias: string, col: string, op: string, value?: string }>} [cfg.whereRows]
 * @param {string[]} [cfg.primaryColumns] — used to skip year/month filters when absent
 * @param {Record<string, string[]|{all?: string[]}>} [cfg.columnMap]
 */
function buildJoinSql(cfg = {}) {
  const tables = Array.isArray(cfg.tables) ? cfg.tables.filter((t) => t?.name) : [];
  if (!tables.length) throw new Error('حداقل یک جدول انتخاب کنید');

  const usedAliases = new Set();
  const normalized = tables.map((t) => {
    let alias = aliasIdent(t.alias);
    if (!alias || usedAliases.has(alias)) alias = pickAlias(usedAliases);
    usedAliases.add(alias);
    const name = ident(t.name);
    if (!name) throw new Error(`نام جدول نامعتبر: ${t.name}`);
    return { name, alias };
  });

  const joins = Array.isArray(cfg.joins) ? cfg.joins : [];
  const selects = Array.isArray(cfg.selects) ? cfg.selects : [];
  const columnMap = cfg.columnMap || {};

  const byRight = new Map();
  joins.forEach((j) => {
    const key = j.rightAlias;
    if (!byRight.has(key)) byRight.set(key, []);
    byRight.get(key).push(j);
  });

  const primary = normalized[0];
  let fromSql = `FROM ${primary.name} ${primary.alias}`;

  normalized.slice(1).forEach((t) => {
    const conds = byRight.get(t.alias) || [];
    const type = (conds[0]?.type || 'LEFT').toUpperCase() === 'INNER' ? 'INNER' : 'LEFT';
    if (!conds.length) {
      throw new Error(`برای جدول ${t.name} حداقل یک شرط JOIN مشخص کنید`);
    }
    const onSql = conds
      .map((c) => {
        const lc = ident(c.leftCol);
        const rc = ident(c.rightCol);
        const la = aliasIdent(c.leftAlias) || primary.alias;
        const ra = aliasIdent(c.rightAlias) || t.alias;
        return `${la}.${lc} = ${ra}.${rc}`;
      })
      .join(' AND ');
    fromSql += `\n${type} JOIN ${t.name} ${t.alias} ON ${onSql}`;
  });

  const usedOut = new Set();
  const selectParts = (selects.length
    ? selects
    : [{ expr: `${primary.alias}.*` }]
  ).map((s) => {
    const expr = String(s.expr || '').trim();
    if (!expr) return null;
    if (/\.\*$/.test(expr)) return expr;
    let outAlias = s.alias || '';
    if (!outAlias) {
      const m = expr.match(/^[a-z]+\.([a-z0-9_]+)$/i);
      const agg = expr.match(/^(SUM|AVG|MAX|MIN|COUNT)\(\s*[a-z]+\.([a-z0-9_]+)\s*\)$/i);
      outAlias = (agg ? agg[2] : m ? m[1] : 'col').toLowerCase();
    }
    outAlias = ident(outAlias).toLowerCase() || 'col';
    if (usedOut.has(outAlias)) {
      const prefix = expr.match(/^([a-z]+)\./i)?.[1] || 'x';
      outAlias = `${prefix}_${outAlias}`;
    }
    usedOut.add(outAlias);
    return `${expr} AS ${outAlias}`;
  }).filter(Boolean);

  const AGG_RE = /\b(SUM|AVG|MAX|MIN|COUNT)\s*\(/i;
  const hasAgg = selectParts.some((p) => AGG_RE.test(p));
  const groupCols = hasAgg
    ? selectParts
      .filter((p) => !AGG_RE.test(p))
      .map((p) => p.replace(/\s+AS\s+\w+$/i, ''))
    : [];

  const where = [];
  const filters = cfg.filters || {};
  const primaryCols = cfg.primaryColumns;
  const pick = (col) => aliasForColumn(normalized, columnMap, primaryCols, col, primary.alias);

  const yearAlias = pick('year_number');
  const monthAlias = pick('month_number');
  const personnelAlias = pick('personnel_id');
  const supervisorAlias = pick('supervisor_id');
  const dcAlias = pick('dc_id');

  if (filters.year !== false && yearAlias) {
    where.push(`${yearAlias}.year_number = :year`);
  }
  if (filters.month !== false && monthAlias) {
    where.push(`${monthAlias}.month_number = :month`);
  }
  if (filters.months && monthAlias) {
    where.push(`${monthAlias}.month_number = ANY(:months)`);
  }
  if (filters.personnel && personnelAlias) {
    where.push(`${personnelAlias}.personnel_id = :personnel_id`);
  }
  if (filters.supervisor && supervisorAlias) {
    where.push(`${supervisorAlias}.supervisor_id = :personnel_id`);
  }
  if (filters.dc && dcAlias) {
    where.push(`${dcAlias}.dc_id = :dc_id`);
  }

  const whereRows = Array.isArray(cfg.whereRows) ? cfg.whereRows : [];
  whereRows.forEach((row) => {
    const sql = whereRowToSql(row, primary.alias);
    if (sql) where.push(sql);
  });

  if (Array.isArray(filters.extra) && filters.extra.length) {
    where.push(...filters.extra.filter((x) => typeof x === 'string' && x.trim()));
  }

  let sql = `SELECT\n  ${selectParts.join(',\n  ')}\n${fromSql}`;
  if (where.length) sql += `\nWHERE ${where.join('\n  AND ')}`;
  if (groupCols.length) sql += `\nGROUP BY ${groupCols.join(', ')}`;
  return sql;
}

function looksMetricKey(key) {
  return /(qty|amount|count|pack|weight|target|percent|rate|sum|net|gross|prize|return|vocher|sell)/i.test(String(key || ''));
}

function selectExprForField(s) {
  const agg = String(s.agg || '').toUpperCase();
  if (s.isMetric && agg && agg !== 'NONE' && agg !== 'VALUE') {
    return `${agg}(${s.alias}.${s.col})`;
  }
  return `${s.alias}.${s.col}`;
}

function qualifySelectColumn(cfg, key) {
  const tables = (cfg?.tables || []).filter((t) => t?.name);
  if (!tables.length) return key;
  const usedAliases = new Set();
  const normalized = tables.map((t) => {
    let alias = aliasIdent(t.alias);
    if (!alias || usedAliases.has(alias)) alias = pickAlias(usedAliases);
    usedAliases.add(alias);
    return { name: ident(t.name), alias };
  });
  const primary = normalized[0];
  const tableAlias = aliasForColumn(
    normalized,
    cfg.columnMap || {},
    cfg.primaryColumns,
    key,
    primary.alias,
  ) || primary.alias;
  const meta = (cfg.selectsMeta || []).find((s) => s.col === key);
  const agg = String(meta?.agg || '').toUpperCase();
  if (meta?.isMetric && agg && agg !== 'NONE' && agg !== 'VALUE') {
    return `${agg}(${tableAlias}.${key})`;
  }
  const queryHasAgg = (cfg.selectsMeta || []).some(
    (s) => s.isMetric && s.agg && s.agg !== 'VALUE' && s.agg !== 'NONE',
  );
  if (queryHasAgg && looksMetricKey(key) && !meta) {
    return `SUM(${tableAlias}.${key})`;
  }
  return `${tableAlias}.${key}`;
}

function selectsFromTableColumns(cfg, columns) {
  const qualify = (key) => qualifySelectColumn(cfg, key);
  const out = [];
  (columns || []).forEach((col) => {
    if (!col) return;
    if (isCustomColumn(col)) {
      const alias = sqlIdent(col.key) || 'custom_1';
      if (col.math) {
        const expr = compileMathToSql(col.math, qualify, mathFieldKeys(col.math, []));
        if (expr) out.push({ expr, alias, isMetric: true });
      } else if (col.template) {
        const expr = compileTemplateToSql(col.template, qualify);
        if (expr) out.push({ expr, alias });
      }
      return;
    }
    if (!col.key) return;
    const meta = (cfg.selectsMeta || []).find((s) => s.col === col.key);
    out.push({
      expr: meta ? selectExprForField(meta) : qualify(col.key),
      alias: sqlIdent(col.key) || col.key,
      isMetric: !!meta?.isMetric,
    });
  });
  return out;
}

function extrasFromColumns(columns, qualify) {
  const extras = [];
  (columns || []).forEach((col) => {
    if (!isCustomColumn(col)) return;
    const alias = sqlIdent(col.key);
    if (!alias) return;
    if (col.math) {
      const expr = compileMathToSql(col.math, qualify, mathFieldKeys(col.math, []));
      if (expr) extras.push({ expr, alias });
    } else if (col.template) {
      const expr = compileTemplateToSql(col.template, qualify);
      if (expr) extras.push({ expr, alias });
    }
  });
  return extras;
}

const DEFAULT_FACT_TABLE = 'sales_calculation_result';

function qualifyForWidget(widget) {
  const cfg = widget?.joinConfig;
  if (cfg?.tables?.length) {
    return (key) => qualifySelectColumn(cfg, key);
  }
  const parsed = parseSqlJoinConfig(getWidgetSql(widget));
  if (!parsed.tables?.length) {
    return (key) => key;
  }
  return (key) => qualifySelectColumn(
    {
      tables: parsed.tables,
      joins: parsed.joins,
      selectsMeta: parsed.selected,
      filters: parsed.filters,
      whereRows: parsed.whereRows,
      primaryColumns: (parsed.selected || []).map((s) => s.col),
      columnMap: widget?.joinConfig?.columnMap,
    },
    key,
  );
}

function seedSqlFromCustomColumns(widget, extras) {
  if (!extras?.length) return '';
  const parsed = parseSqlJoinConfig(getWidgetSql(widget));
  const fromJoin = widget?.joinConfig?.tables?.[0];
  const fromParsed = parsed.tables?.[0];
  const table = fromJoin || fromParsed || { name: DEFAULT_FACT_TABLE, alias: 'a' };
  try {
    return buildJoinSql({
      tables: [table],
      joins: [],
      selects: extras.map((e) => ({ expr: e.expr, alias: e.alias, isMetric: true })),
      filters: { year: true, month: true },
      primaryColumns: ['year_number', 'month_number'],
    });
  } catch {
    const parts = extras.filter((e) => e.expr && e.alias).map((e) => `${e.expr} AS ${e.alias}`);
    if (!parts.length) return '';
    const name = String(table.name || DEFAULT_FACT_TABLE).replace(/[^a-zA-Z0-9_]/g, '') || DEFAULT_FACT_TABLE;
    const alias = String(table.alias || 'a').replace(/[^a-z]/gi, '') || 'a';
    return `SELECT\n  ${parts.join(',\n  ')}\nFROM ${name} ${alias}`;
  }
}

/** Merge custom-column math/templates into a SQL SELECT list. */
function applyCustomColumnsToSql(sql, columns, qualify) {
  return mergeComputedSelects(sql, extrasFromColumns(columns, qualify));
}

/** Insert or replace `expr AS alias` items in an existing SELECT list. */
function mergeComputedSelects(sql, extras) {
  const text = String(sql || '');
  if (!extras?.length) {
    return text.replace(
      /^([\s\S]*?\bselect\s+)([\s\S]*?)(\s+from\s)/i,
      (full, pre, body, fromKw) => {
        const parts = splitSelectList(body).filter((p) => {
          const as = /\bas\s+(custom_\d+)\s*$/i.exec(p);
          return !as;
        });
        return parts.length ? `${pre}${parts.join(',\n  ')}${fromKw}` : full;
      },
    );
  }
  const m = text.match(/^([\s\S]*?\bselect\s+)([\s\S]*?)(\s+from\s)([\s\S]*)$/i);
  if (!m) return text;
  const extraAliases = new Set(extras.map((e) => String(e.alias).toLowerCase()));
  const kept = splitSelectList(m[2]).filter((p) => {
    const as = /\bas\s+([a-zA-Z_][\w]*)\s*$/i.exec(p);
    const alias = (as ? as[1] : '').toLowerCase();
    return !extraAliases.has(alias);
  });
  extras.forEach((e) => {
    if (e.expr && e.alias) kept.push(`${e.expr} AS ${e.alias}`);
  });
  return `${m[1]}${kept.join(',\n  ')}${m[3]}${m[4]}`;
}

function sqlUsesPeriodParams(sql) {
  const text = String(sql || '');
  return PERIOD_PARAM_RE.test(text) || PERIOD_BRACE_RE.test(text);
}

function stripSqlComments(sql) {
  return String(sql || '')
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\n]*/g, ' ');
}

function stripLeadingWith(sql) {
  if (!/^\s*with\b/i.test(sql)) return sql.trim();
  let rest = sql.replace(/^\s*with\b/i, '');
  for (;;) {
    const m = rest.match(/^\s*([a-z_][a-z0-9_]*)\s*(?:\([^)]*\))?\s*as\s*\(/i);
    if (!m) break;
    let depth = 0;
    let j = m[0].length - 1;
    for (; j < rest.length; j += 1) {
      if (rest[j] === '(') depth += 1;
      else if (rest[j] === ')') {
        depth -= 1;
        if (depth === 0) {
          j += 1;
          break;
        }
      }
    }
    rest = rest.slice(j);
    const comma = rest.match(/^\s*,/);
    if (comma) {
      rest = rest.slice(comma[0].length);
      continue;
    }
    break;
  }
  return rest.trim();
}

function splitSelectList(body) {
  const parts = [];
  let buf = '';
  let depth = 0;
  let quote = '';
  const text = String(body || '');
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      buf += ch;
      if (ch === quote) {
        if (quote === '"' && text[i + 1] === '"') {
          buf += text[i + 1];
          i += 1;
        } else {
          quote = '';
        }
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === '(') depth += 1;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      parts.push(buf.trim());
      buf = '';
    } else {
      buf += ch;
    }
  }
  if (buf.trim()) parts.push(buf.trim());
  return parts;
}

function splitByKeyword(sql, keywords) {
  const found = {};
  let depth = 0;
  let i = 0;
  const starts = [];
  const s = String(sql || '');
  while (i < s.length) {
    const ch = s[i];
    if (ch === "'") {
      i += 1;
      while (i < s.length) {
        if (s[i] === "'" && s[i + 1] === "'") { i += 2; continue; }
        if (s[i] === "'") { i += 1; break; }
        i += 1;
      }
      continue;
    }
    if (ch === '(') { depth += 1; i += 1; continue; }
    if (ch === ')') { depth = Math.max(0, depth - 1); i += 1; continue; }
    if (depth === 0) {
      let hit = null;
      for (const kw of keywords) {
        const re = new RegExp(`^\\b${kw}\\b`, 'i');
        if (re.test(s.slice(i))) {
          hit = { key: kw, at: i, len: kw.length };
          break;
        }
      }
      if (hit) {
        starts.push(hit);
        i += hit.len;
        continue;
      }
    }
    i += 1;
  }
  if (!starts.length) return { rest: s.trim(), parts: found };
  found._before = s.slice(0, starts[0].at).trim();
  for (let idx = 0; idx < starts.length; idx += 1) {
    const end = idx + 1 < starts.length ? starts[idx + 1].at : s.length;
    const key = starts[idx].key.toLowerCase().replace(/\s+/g, '_');
    found[key] = s.slice(starts[idx].at + starts[idx].len, end).trim();
  }
  return { rest: found._before || '', parts: found };
}

function parseTableRef(chunk) {
  const t = String(chunk || '').replace(/^LATERAL\s+/i, '').trim();
  if (!t || t.startsWith('(')) return null;
  const m = t.match(/^(?:([a-zA-Z_][\w]*)\.)?([a-zA-Z_][\w]*)(?:\s+(?:AS\s+)?([a-zA-Z_][\w]*))?/i);
  if (!m) return null;
  const name = m[2];
  const alias = m[3] || name;
  if (!name) return null;
  return { name, alias };
}

function joinKind(typeRaw) {
  const t = String(typeRaw || '').toUpperCase().replace(/\s+/g, ' ');
  if (t.includes('LEFT')) return 'LEFT';
  return 'INNER';
}

function splitFromJoins(fromSql) {
  const chunks = [];
  let lastType = 'FROM';
  let lastIndex = 0;
  let depth = 0;
  let i = 0;
  const s = String(fromSql || '');
  const joinAt = (idx) => {
    const rest = s.slice(idx);
    return rest.match(/^((?:INNER|LEFT|RIGHT|FULL)(?:\s+OUTER)?\s+JOIN|CROSS\s+JOIN|JOIN)\b/i);
  };
  while (i < s.length) {
    const ch = s[i];
    if (ch === "'") {
      i += 1;
      while (i < s.length) {
        if (s[i] === "'" && s[i + 1] === "'") { i += 2; continue; }
        if (s[i] === "'") { i += 1; break; }
        i += 1;
      }
      continue;
    }
    if (ch === '(') { depth += 1; i += 1; continue; }
    if (ch === ')') { depth = Math.max(0, depth - 1); i += 1; continue; }
    if (depth === 0) {
      const m = joinAt(i);
      if (m) {
        chunks.push({ type: lastType, text: s.slice(lastIndex, i).trim() });
        lastType = m[1];
        i += m[0].length;
        lastIndex = i;
        continue;
      }
    }
    i += 1;
  }
  chunks.push({ type: lastType, text: s.slice(lastIndex).trim() });
  return chunks.filter((c) => c.text);
}

function parseOnPairs(onSql, fallbackRightAlias) {
  const pairs = [];
  const eqRe = /([a-zA-Z_][\w]*)\.([a-zA-Z_][\w]*)\s*=\s*([a-zA-Z_][\w]*)\.([a-zA-Z_][\w]*)/g;
  let m;
  const text = String(onSql || '');
  while ((m = eqRe.exec(text))) {
    let leftAlias = m[1];
    let leftCol = m[2];
    let rightAlias = m[3];
    let rightCol = m[4];
    if (fallbackRightAlias && leftAlias === fallbackRightAlias) {
      leftAlias = m[3];
      leftCol = m[4];
      rightAlias = m[1];
      rightCol = m[2];
    }
    pairs.push({
      leftAlias,
      leftCol,
      rightAlias,
      rightCol,
    });
  }
  return pairs;
}

function parseSelectFields(selectSql, fallbackAlias = '') {
  const selected = [];
  const body = String(selectSql || '').trim();
  if (!body || body === '*') return selected;
  splitSelectList(body).forEach((part) => {
    const aggM = part.match(
      /^(SUM|AVG|MAX|MIN|COUNT)\s*\(\s*(?:\*|([a-zA-Z_][\w]*)(?:\.([a-zA-Z_][\w]*))?)\s*\)(?:\s+AS\s+[a-zA-Z_\u0600-\u06FF][\w\u0600-\u06FF]*)?$/i,
    );
    if (aggM) {
      const alias = aggM[3] ? aggM[2] : fallbackAlias;
      const col = aggM[3] || aggM[2];
      if (!alias || !col) return;
      selected.push({
        alias,
        col,
        isMetric: true,
        agg: aggM[1].toUpperCase(),
      });
      return;
    }
    const colM = part.match(
      /^([a-zA-Z_][\w]*)\.([a-zA-Z_][\w]*)(?:\s+AS\s+[a-zA-Z_\u0600-\u06FF][\w\u0600-\u06FF]*)?$/i,
    );
    if (colM) {
      selected.push({
        alias: colM[1],
        col: colM[2],
        isMetric: false,
        agg: 'VALUE',
      });
      return;
    }
    const bare = part.match(
      /^([a-zA-Z_][\w]*)(?:\s+AS\s+[a-zA-Z_\u0600-\u06FF][\w\u0600-\u06FF]*)?$/i,
    );
    if (bare && fallbackAlias) {
      selected.push({
        alias: fallbackAlias,
        col: bare[1],
        isMetric: false,
        agg: 'VALUE',
      });
    }
  });
  return selected;
}

function splitAndClauses(sql) {
  const parts = [];
  let buf = '';
  let depth = 0;
  const s = String(sql || '');
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '(') depth += 1;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && /\sAND\s/i.test(s.slice(i, i + 5))) {
      parts.push(buf.trim());
      buf = '';
      i += 4;
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) parts.push(buf.trim());
  return parts;
}

function parseWhereFilters(whereSql) {
  const filters = {
    year: false,
    month: false,
    months: false,
    personnel: false,
    supervisor: false,
    dc: false,
  };
  const whereRows = [];
  if (!whereSql) return { filters, whereRows };
  splitAndClauses(whereSql).forEach((raw) => {
    const t = raw.trim().replace(/^\(+/, '').replace(/\)+$/, '');
    if (/\byear_number\s*=\s*:year\b/i.test(t)) { filters.year = true; return; }
    if (/\bmonth_number\s*=\s*:month\b/i.test(t)) { filters.month = true; return; }
    if (/\bmonth_number\s*=\s*ANY\s*\(\s*:months\s*\)/i.test(t)) { filters.months = true; return; }
    if (/\bpersonnel_id\s*=\s*:personnel_id\b/i.test(t)) { filters.personnel = true; return; }
    if (/\bsupervisor_id\s*=\s*:personnel_id\b/i.test(t)) { filters.supervisor = true; return; }
    if (/\bdc_id\s*=\s*:dc_id\b/i.test(t)) { filters.dc = true; return; }
    const row = t.match(
      /^([a-zA-Z_][\w]*)\.([a-zA-Z_][\w]*)\s*(=|!=|<>|>=|<=|>|<|LIKE|NOT LIKE|IS NULL|IS NOT NULL)\s*(.*)$/i,
    );
    if (!row) return;
    const op = normalizeWhereOp(row[3]);
    let value = (row[4] || '').trim();
    if (op === 'IS NULL' || op === 'IS NOT NULL') value = '';
    else if (/^'.*'$/.test(value)) value = value.slice(1, -1).replace(/''/g, "'");
    whereRows.push({ alias: row[1], col: row[2], op, value });
  });
  return { filters, whereRows };
}

/**
 * Reconstruct visual join tables/fields/joins from pasted SQL (best-effort).
 * Does not rewrite the SQL; used only to populate the query-builder UI.
 */
function parseSqlJoinConfig(sql) {
  const empty = {
    tables: [],
    selected: [],
    joins: [],
    filters: {
      year: false, month: false, months: false, supervisor: false, personnel: false, dc: false,
    },
    whereRows: [],
  };
  const raw = stripSqlComments(sql);
  if (!raw.trim()) return empty;
  const body = stripLeadingWith(raw);
  const selectMatch = body.match(/^\s*SELECT\s+([\s\S]+)$/i);
  if (!selectMatch) return empty;
  const { rest: selectList, parts } = splitByKeyword(selectMatch[1], [
    'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'LIMIT', 'HAVING',
  ]);
  const fromSql = parts.from || '';
  if (!fromSql) return empty;

  const tables = [];
  const joins = [];
  splitFromJoins(fromSql).forEach((chunk, idx) => {
    let tableText = chunk.text;
    let onSql = '';
    if (idx > 0) {
      const onM = chunk.text.match(/^([\s\S]+?)\s+ON\s+([\s\S]+)$/i);
      if (onM) {
        tableText = onM[1];
        onSql = onM[2];
      }
    }
    const table = parseTableRef(tableText);
    if (!table) return;
    tables.push(table);
    if (idx > 0 && onSql) {
      const type = joinKind(chunk.type);
      parseOnPairs(onSql, table.alias).forEach((p) => {
        joins.push({ type, ...p, rightAlias: table.alias });
      });
    }
  });

  const selected = parseSelectFields(selectList, tables[0]?.alias);
  const { filters, whereRows } = parseWhereFilters(parts.where || '');
  return { tables, selected, joins, filters, whereRows };
}

function unquoteSqlIdent(raw) {
  const t = String(raw || '').trim();
  const dq = /^"((?:[^"]|"")*)"$/.exec(t);
  if (dq) return dq[1].replace(/""/g, '"');
  return t;
}

function aliasFromSelectPart(part) {
  const text = String(part || '').trim();
  if (!text || text === '*' || /^[a-z_][a-z0-9_]*\.\*$/i.test(text)) return '';
  const asQuoted = /\bas\s+("(?:[^"]|"")*")\s*$/i.exec(text);
  if (asQuoted) return unquoteSqlIdent(asQuoted[1]);
  const asIdent = /\bas\s+([\p{L}_][\p{L}\p{N}_]*)\s*$/iu.exec(text);
  if (asIdent) return asIdent[1];
  const trailingQuoted = /("(?:[^"]|"")*")\s*$/.exec(text);
  if (trailingQuoted && !/\bas\s+$/i.test(text.slice(0, trailingQuoted.index))) {
    return unquoteSqlIdent(trailingQuoted[1]);
  }
  const noAgg = text.replace(/^(SUM|AVG|MAX|MIN|COUNT)\s*\(\s*/i, '').replace(/\s*\)\s*$/, '');
  const ident = (noAgg.split('.').pop() || '').trim();
  if (/^"(?:[^"]|"")*"$/.test(ident)) return unquoteSqlIdent(ident);
  return ident.replace(/[^\p{L}\p{N}_]/gu, '');
}

function parseSelectListKeys(sqlText) {
  const body = stripLeadingWith(stripSqlComments(sqlText));
  const m = body.match(/^\s*select\s+(?:distinct\s+(?:on\s*\([^)]*\)\s+)?)?([\s\S]*?)\s+from\s/i);
  if (!m) return [];
  const list = m[1].trim();
  if (!list || list === '*' || /^\*\s*(,|$)/.test(list)) return [];
  return splitSelectList(list).map(aliasFromSelectPart).filter(Boolean);
}

function extractCteBody(sql, cteName) {
  const raw = String(sql || '');
  const name = String(cteName || '').trim();
  if (!name || !/^\s*with\b/i.test(raw)) return '';
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\b${escaped}\\s*(?:\\([^)]*\\))?\\s*as\\s*\\(`, 'i');
  const m = re.exec(raw);
  if (!m) return '';
  let depth = 0;
  const open = m.index + m[0].length - 1;
  for (let j = open; j < raw.length; j += 1) {
    if (raw[j] === '(') depth += 1;
    else if (raw[j] === ')') {
      depth -= 1;
      if (depth === 0) return raw.slice(open + 1, j);
    }
  }
  return '';
}

/** Output aliases from the outer SELECT (skips WITH CTEs). Quoted/Persian AS names kept. */
function parseSqlSelectKeys(sql) {
  const keys = parseSelectListKeys(sql);
  if (keys.length) return keys;
  const stripped = stripSqlComments(sql);
  const body = stripLeadingWith(stripped);
  const star = body.match(/^\s*select\s+(?:distinct\s+)?\*\s+from\s+("(?:[^"]|"")*"|[A-Za-z_][A-Za-z0-9_]*)/i);
  if (!star) return [];
  return parseSelectListKeys(extractCteBody(stripped, unquoteSqlIdent(star[1])));
}

/** True when SQL has no parseable outer aliases (SELECT * / UNION / WITH). */
function sqlSelectNeedsDiscover(sql) {
  return !!String(sql || '').trim() && parseSqlSelectKeys(sql).length === 0;
}

function getWidgetSql(widget) {
  const direct = String(widget?.sqlQuery || widget?.query?.sql || '').trim();
  if (direct) return direct;
  if (widget?.joinConfig?.tables?.length) {
    try {
      return String(sqlFromJoinConfig(widget.joinConfig, widget.tableConfig?.columns) || '').trim();
    } catch {
      return '';
    }
  }
  return '';
}

/** Rebuild SQL from the visual joinConfig stored on a widget. */
function sqlFromJoinConfig(cfg, tableColumns) {
  if (!cfg?.tables?.length) return '';
  const fromTable = Array.isArray(tableColumns) && tableColumns.length
    ? selectsFromTableColumns(cfg, tableColumns)
    : [];
  const selects = fromTable.length
    ? fromTable
    : (cfg.selectsMeta || []).map((s) => ({
      expr: selectExprForField(s),
      alias: s.col,
      isMetric: !!s.isMetric,
    }));
  return buildJoinSql({
    tables: cfg.tables,
    joins: cfg.joins,
    selects: selects.length ? selects : undefined,
    filters: cfg.filters,
    whereRows: cfg.whereRows,
    primaryColumns: cfg.primaryColumns,
    columnMap: cfg.columnMap,
  });
}

/** Rebuild widget SQL from table column config (custom math becomes SELECT expr). */
function syncWidgetSqlFromTableColumns(widget, columns) {
  const qualify = qualifyForWidget(widget);
  const extras = extrasFromColumns(columns, qualify);
  const current = getWidgetSql(widget);
  if (current) return mergeComputedSelects(current, extras);
  const cfg = widget?.joinConfig;
  if (cfg?.tables?.length) {
    try {
      return sqlFromJoinConfig(cfg, columns);
    } catch {
      return seedSqlFromCustomColumns(widget, extras);
    }
  }
  return seedSqlFromCustomColumns(widget, extras);
}

/** Ensure custom-column math is compiled into sqlQuery before /reports/run. */
function prepareWidgetForRun(widget) {
  const columns = widget?.tableConfig?.columns;
  if (!widget || !Array.isArray(columns) || !columns.some(isCustomColumn)) return widget;
  const sql = syncWidgetSqlFromTableColumns(widget, columns);
  if (!sql) return widget;
  const current = getWidgetSql(widget);
  if (sql === current) return widget;
  return {
    ...widget,
    sqlQuery: sql,
    query: { ...(widget.query || {}), sql },
  };
}

export {
  buildJoinSql,
  suggestJoins,
  suggestJoinsFromTables,
  selectExprForField,
  sqlFromJoinConfig,
  syncWidgetSqlFromTableColumns,
  applyCustomColumnsToSql,
  prepareWidgetForRun,
  mergeComputedSelects,
  parseSqlSelectKeys,
  sqlSelectNeedsDiscover,
  parseSqlJoinConfig,
  sqlUsesPeriodParams,
  getWidgetSql,
  whereRowToSql,
  JOIN_HINT_COLS,
  JOIN_EQUIV,
  WHERE_OPS,
  SESSION_PARAMS,
};
